import { LINEAR_CURVE } from '../../core/params/curve';
import { DEFAULT_PALETTE } from '../../core/palette';
import type { ParamSchema, SchemaValues } from '../../core/params/types';

export const HATCHING_PARAMS = [
  {
    kind: 'int',
    key: 'layers',
    label: 'Schraffurlagen',
    min: 1,
    max: 4,
    step: 1,
    default: 1,
    group: 'Schraffur',
    tooltip: 'Mehrere Lagen ergeben Kreuzschraffur.',
  },
  {
    kind: 'float',
    key: 'angle',
    label: 'Winkel',
    min: -90,
    max: 90,
    step: 0.5,
    default: 38,
    unit: '°',
    group: 'Schraffur',
  },
  {
    kind: 'float',
    key: 'layerAngle',
    label: 'Winkel je Lage',
    min: 5,
    max: 90,
    step: 1,
    default: 55,
    unit: '°',
    group: 'Schraffur',
    tooltip: 'Winkelabstand zwischen aufeinanderfolgenden Lagen.',
    showIf: (v) => (v.layers as number) > 1,
  },
  {
    kind: 'float',
    key: 'spacing',
    label: 'Linienabstand',
    min: 1,
    max: 60,
    step: 0.25,
    scale: 'log',
    default: 7,
    unit: 'px',
    bulkRange: [3, 22],
    group: 'Schraffur',
  },
  {
    kind: 'float',
    key: 'lineWidth',
    label: 'Strichstärke',
    min: 0.2,
    max: 14,
    step: 0.1,
    default: 1.4,
    unit: 'px',
    group: 'Schraffur',
  },
  {
    kind: 'float',
    key: 'overshoot',
    label: 'Überstand',
    min: 0,
    max: 80,
    step: 0.5,
    default: 0,
    unit: 'px',
    bulkRange: [0, 30],
    group: 'Schraffur',
    tooltip: 'Wie weit die Striche über die Kontur hinausschiessen — der Skizzen-Effekt.',
  },
  {
    kind: 'enum',
    key: 'region',
    label: 'Bereich',
    default: 'inside',
    segmented: true,
    group: 'Schraffur',
    options: [
      { value: 'inside', label: 'Innen' },
      { value: 'outside', label: 'Aussen' },
    ],
  },

  {
    kind: 'float',
    key: 'waveAmplitude',
    label: 'Wellenamplitude',
    min: 0,
    max: 40,
    step: 0.25,
    default: 0,
    unit: 'px',
    bulkRange: [0, 12],
    group: 'Form',
    tooltip: 'Lässt die Striche wellen statt gerade zu laufen.',
  },
  {
    kind: 'float',
    key: 'waveFrequency',
    label: 'Wellenfrequenz',
    min: 0.2,
    max: 20,
    step: 0.1,
    default: 3,
    group: 'Form',
    showIf: (v) => (v.waveAmplitude as number) > 0,
  },
  {
    kind: 'curve',
    key: 'densityCurve',
    label: 'Dichte nach Tiefe',
    default: LINEAR_CURVE,
    xLabel: 'Rand',
    yLabel: 'Kern',
    group: 'Form',
    tooltip: 'Steuert, welcher Anteil eines Strichs abhängig von der Tiefe im Glyphen gezeichnet wird.',
  },
  {
    kind: 'float',
    key: 'depthReach',
    label: 'Tiefen-Reichweite',
    min: 2,
    max: 300,
    step: 1,
    scale: 'log',
    default: 40,
    unit: 'px',
    group: 'Form',
  },

  {
    kind: 'float',
    key: 'opacity',
    label: 'Deckkraft',
    min: 0.05,
    max: 1,
    step: 0.01,
    default: 0.95,
    group: 'Farbe',
  },
  {
    kind: 'enum',
    key: 'colorMode',
    label: 'Farbmodus',
    default: 'depth',
    segmented: true,
    group: 'Farbe',
    options: [
      { value: 'solid', label: 'Einfarbig' },
      { value: 'depth', label: 'Tiefe' },
      { value: 'layer', label: 'Lage' },
    ],
  },
  {
    kind: 'palette',
    key: 'palette',
    label: 'Palette',
    default: { ...DEFAULT_PALETTE, paletteId: 'mono', reverse: true },
    group: 'Farbe',
  },
] as const satisfies ParamSchema;

export type HatchingValues = SchemaValues<typeof HATCHING_PARAMS>;
